/**
 * Sierra Chart fills → paired round trips.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` `parseSierraFills` /
 * `makeImportedTrade` — semantics, not code (D3).
 *
 * Sierra exports one row per FILL. Pairing them into trades is where import
 * bugs live: a partial close, a same-direction add, or a reversal each take a
 * different path, and getting one wrong produces a trade with plausible numbers
 * rather than an error.
 */
import { consumePrior, type PriorPosition } from './carryIn';
import { dedupeFillsById, getCol, numCol, type Row } from './columns';
import { parseDateOnly, parseTimeOnly } from './delimited';
import { findContract, stripSymbolDecorations, type ContractSpec } from '../../domain/trade';

/** A paired round trip, in the flat shape `domain/types` uses. */
export interface ImportedTrade {
  date: string;
  timeIn: string;
  timeOut: string;
  contract: string;
  contractFull: string | null;
  side: 'long' | 'short';
  qty: number;
  entry: number;
  exit: number;
  points: number;
  fees: number;
  grossPnl: number;
  netPnl: number;
  sourceAccount: string | null;
  /**
   * Broker fill ids.
   *
   * ⚠ WRITE-ONLY TODAY. The original's comment says these let the dedupe
   * fingerprint distinguish two trades sharing every visible field. They do
   * not: `tradeFingerprint` is `contract|date|timeIn|timeOut|side|qty|entry|exit`
   * with no ids, `addImportedTradesDedupe` states it "matches by content alone",
   * and the field appears in no migration — so it is dropped on the cloud
   * round-trip. Carried for parity, not because anything reads it.
   */
  sourceFillIds: { open: string | null; close: string | null } | null;
  maePoints: number | null;
  mfePoints: number | null;
  maeSecs: number | null;
  mfeSecs: number | null;
  /**
   * True when NO real source fee was supplied, so the account's commission
   * schedule may fill it in. A source-provided fee counts as manual and must
   * not be overridden.
   */
  feesAuto: boolean;
  /**
   * Per-fill breakdown behind a collapsed round trip. Present ONLY from the
   * position-episode parsers (ATAS/TT), absent from the FIFO parsers — the
   * original attaches the key conditionally and downstream tests for it.
   */
  executions?: Execution[];
  /**
   * Which contract's tape MAE/MFE was measured from, when the source said (the
   * ATAS logger's `PriceSource` column). ⚠ `undefined` means UNSTATED, not "the
   * traded contract" — every file written before 2026-08-14 is that case.
   * Routed into `extras` by `tradeToDb`, like `executions`.
   */
  excursionSource?: string | null;
}

export interface OpenPosition {
  account: string;
  /** The catalogue ROOT — `MNQ`. Not enough to close against; see below. */
  contract: string;
  /**
   * 🚨 THE SYMBOL AS THE FILE WROTE IT, normalised — `MNQU6`, or `MNQU6.CME`
   * from Sierra. The same value the trade carries.
   *
   * `contract` is the root, which is the right key for grouping and reporting
   * and the WRONG key for deciding whether a fill can close this position. A
   * December fill and a September position share the root `MNQ` and are two
   * different instruments; matching on the root would let one close the other
   * and move the trade by the whole calendar spread — 20–40 points on ES near a
   * roll. That is the Databento `*.C.0` roll bug and tracker #25 arriving a
   * third way, so it is carried BEFORE anything is built that could walk in.
   *
   * ⚠ TWO THINGS A MATCH (#12) MUST NOT ASSUME, both measured:
   *   1. **It may carry no month at all.** An export that writes a bare `MNQ`
   *      yields `MNQ` here — identical to the root. "No month stated" is not
   *      "the front month", and a match must treat it as unknown.
   *   2. **It is not normalised ACROSS sources.** `stripSymbolDecorations`
   *      strips a hyphenated exchange (`-CME`) and not a dotted one, so the
   *      same instrument is `MNQU6.CME` from Sierra and `MNQU6` from ATAS. A
   *      match comparing these raw would silently never fire across sources.
   *
   * ⚠ Carried but NOT added to `fingerprint` — see `openPositionFingerprint`.
   */
  contractFull: string | null;
  side: 'long' | 'short';
  openQty: number;
  avgEntry: number;
  openTime: string;
  /**
   * Commission already paid to OPEN this position, attached to no trade.
   *
   * 🚨 REAL MONEY THAT IS OTHERWISE LOST. This fill never became a trade, so
   * nothing has counted its fee. A later file that closes the position needs it
   * or the cross-session trade is reported at half its true cost — and an import
   * path that quietly counted fees at zero is a mistake this project has already
   * shipped once (D160).
   *
   * ⚠ Carried but NOT in `fingerprint` — see `openPositionFingerprint`.
   */
  openFee: number;
  /** Stable identity so re-importing the same unfinished position doesn't duplicate it. */
  fingerprint: string;
}

export interface SierraResult {
  trades: ImportedTrade[];
  openPositions: OpenPosition[];
  /** Rows that carried no usable fill data. Surfaced in the import preview. */
  skipped: number;
  /**
   * Contracts that CLOSED a position opened before this file starts — an
   * overnight hold, or a part-day export.
   *
   * 🚨 Reported rather than paired. See the comment at the guard: pairing them
   * opens a phantom position on the opposite side and shifts the entire rest of
   * the day by one leg. Only the fills path can produce this; the trade-list
   * path is already paired by Sierra.
   */
  carriedIn: number;
  /**
   * How many fills carried a `PositionQuantity` we could check ourselves
   * against.
   *
   * ⚠ RETURNED BESIDE THE MISMATCHES ON PURPOSE. On a real Sierra log this is
   * ~14% of rows, so zero mismatches out of ten checks is a spot-check passing,
   * not a day being verified. Reporting the denominator is what stops the
   * former being read as the latter.
   */
  positionChecked: number;
  /** Fills where the file's position and ours disagreed. Each is a real bug. */
  positionMismatches: PositionMismatch[];
  /**
   * Fill rows Sierra pulled back from the broker after the fact rather than
   * recording live — `OrderActionSource` contains "historical fill".
   *
   * 🚨 ON THOSE ROWS `OpenClose` IS NOT EVIDENCE. Measured on Josh's real
   * 2026-08-05 export: 55 of its 72 fills are backfills, and **every one of them
   * says `Open`** — including the sells in an alternating
   * `10 Sell → 10 Buy → 10 Sell → 10 Buy` scalp sequence, where at minimum half
   * must be closes. They also carry no `InternalOrderID`, no `PositionQuantity`,
   * and whole-second timestamps with a synthetic `.000000` / `.000001` tail.
   *
   * ⚠ A column that is PRESENT AND WRONG is worse than one that is absent, and
   * this is what makes it dangerous: both defences go blind on exactly these
   * rows. The carried-in guard fires only on an explicit `Close`, which they
   * never say; the `PositionQuantity` cross-check fires only where that column
   * is populated, which it never is. See tracker #35.
   */
  unverifiedFills: number;
  /** Fill rows total, so `unverifiedFills` reads as a share and not a scare. */
  totalFills: number;
  /**
   * 🚨 A SECOND, INDEPENDENT CROSS-CHECK ON THE PAIRING — and it catches D145 at
   * the failing row.
   *
   * ⚠ THE COVERAGE CLAIM, MEASURED RATHER THAN ASSUMED. On Josh's real
   * 2026-08-05 log this check can use **16** of 71 fills (the verifiable ones)
   * against `PositionQuantity`'s **10**, and the two overlap — so it adds six
   * rows of coverage and a second route to the same finding, not a wholly new
   * population. Both fire on the 09:40:20 row with the carried-in guard removed.
   *
   * The file labels every fill Open or Close. When we CLOSE a leg with a fill
   * the file calls an **Open**, our pairing and the exporter disagree about what
   * that fill did. On Josh's 2026-08-05 log that is exactly the 09:40:20 buy:
   * marked `Open`, `PositionQuantity=5`, and the old parser spent it closing a
   * phantom short — which is why the day showed zero MNQ longs.
   *
   * ⚠ ONLY ON VERIFIABLE ROWS, because a backfill row says `Open` on everything
   * and would make this fire on every close in the file.
   *
   * ⚠ AND ONLY ON A PURE CLOSE — a reversing fill genuinely closes and opens in
   * one row, so `Open` on it is not a contradiction. Requiring the fill to be
   * fully consumed by closing is what keeps this from crying wolf.
   */
  openCloseConflicts: OpenCloseConflict[];
  /**
   * Positions carried in from an earlier import that THIS file closed.
   *
   * 🚨 Every one is a trade that would otherwise have been a phantom on the
   * opposite side. Surfaced in the import preview because a completed
   * cross-session trade is worth seeing — and because the entry price came from
   * a different file, which the trader should be told rather than left to
   * discover.
   */
  carriedInClosed: CarriedInClosed[];
}

export interface CarriedInClosed {
  time: string;
  contract: string;
  account: string;
  qty: number;
  /** When the position was opened — in an earlier file. */
  openedAt: string;
  fingerprint: string;
}

export interface OpenCloseConflict {
  time: string;
  contract: string;
  account: string;
  /** What the file called this fill. */
  fileSays: string;
  /** What our pairing did with it. */
  weDid: 'closed a position';
  qty: number;
}

/**
 * Can this row's `OpenClose` be believed?
 *
 * Sierra's `OrderActionSource` says how the row reached the log. A live fill
 * reads `Rithmic Direct - DTC (Filled)` and carries an order id, a true
 * microsecond stamp and usually a `PositionQuantity`. A row re-pulled from the
 * broker afterwards reads `Rithmic Direct - DTC historical fill` and has none of
 * those — and stamps `Open` on everything, closes included.
 *
 * ⚠ DEFAULTS TO TRUSTED. An export with no `OrderActionSource` column at all
 * (every other Sierra shape, and every other broker's) must behave exactly as it
 * did before — this only withdraws trust where the file positively identifies
 * itself as a backfill. Widening it to "anything unfamiliar" would silently
 * change every existing import.
 */
export function fillProvenanceIsVerifiable(source: string | null | undefined): boolean {
  const s = String(source ?? '').toLowerCase();
  if (!s) return true;
  return !(s.includes('historical') || s.includes('backfill'));
}

export interface PositionMismatch {
  time: string;
  contract: string;
  account: string;
  /** What the exporter says the position was after this fill. */
  expected: number;
  /** What our FIFO queue holds. */
  held: number;
}

interface QueueItem {
  side: 'long' | 'short';
  qty: number;
  entry: number;
  openTime: string;
  fee: number;
  fillId: string;
  contract: string;
  /** The contract WITH its month — see `OpenPosition.contractFull`. */
  contractFull: string | null;
  account: string;
  dpp: number;
}

interface BuildArgs {
  date: string; timeIn: string; timeOut: string;
  contract: string; contractFull: string | null;
  side: 'long' | 'short'; qty: number; entry: number; exit: number;
  fees: number; netPnl: number | null; dpp: number;
  /**
   * The caller's `netPnl` is ground truth — do NOT sign-repair it.
   *
   * Set by the position-episode walker, whose `realized` is accumulated
   * leg-by-leg from the actual fills. Not set by sources whose P&L genuinely
   * needs checking (Tradovate's absolute-value `pnl`, the ATAS Journal sheet).
   */
  netAuthoritative?: boolean;
  sourceAccount: string | null;
  openFillId: string | null; closeFillId: string | null;
  maePoints: number | null; mfePoints: number | null;
  maeSecs: number | null; mfeSecs: number | null;
  /**
   * Which contract's tape MAE/MFE was measured from — usually the traded contract,
   * but on a cross-traded book (chart + data on the mini, orders filled as the
   * micro) it is the mini, and the excursion is genuinely a neighbour's. Null for
   * every source that does not say, which is all of them except the ATAS logger.
   */
  excursionSource?: string | null | undefined;
  /** Per-fill breakdown, from the position-episode parsers only. */
  executions?: Execution[] | undefined;
}

/** One underlying fill behind a collapsed round trip. */
export interface Execution {
  time: string;
  side: 'buy' | 'sell';
  qty: number;
  price: number;
}

/**
 * Build one imported trade, reconciling a source-supplied net P&L against what
 * entry/exit imply.
 *
 * ⚠ THE SIGN FIX. Some sources — notably Tradovate's Performance Report —
 * export P&L as an ABSOLUTE value, so a losing trade arrives positive. The
 * magnitude is trusted (it includes commissions and source-specific
 * adjustments) but the SIGN is verified against `gross − fees`.
 *
 * Compared against expected NET, not raw points, deliberately: that avoids
 * wrongly flipping the case where fees exceed a small gross profit
 * (+0.5pt × $50 = +$25 gross, −$30 fees = −$5 net — a points-based check would
 * flip a correctly-supplied −$5). Skipped entirely when `computedGross` is 0,
 * because an unknown contract gives no ground truth to compare against.
 *
 * DELIBERATELY NOT HERE, unlike the original `makeImportedTrade`: id
 * generation, account resolution, session derivation, and applying the firm
 * commission schedule. Those need app state or a clock; keeping them out is
 * what makes this function testable at all. The caller assembles the Trade.
 */
export function buildImportedTrade(d: BuildArgs): ImportedTrade {
  const points = d.side === 'long' ? d.exit - d.entry : d.entry - d.exit;
  const fees = d.fees || 0;
  const dpp = d.dpp || 0;
  const computedGross = points * dpp * d.qty;

  // ⚠ SKIPPED ENTIRELY when the caller's net is AUTHORITATIVE.
  //
  // The repair assumes `computedGross` is ground truth. For the position-episode
  // walker it is not: `st.realized` accumulates leg-by-leg against the RUNNING
  // entry VWAP at each reduce, while computedGross is reconstructed from the
  // episode's FINAL VWAPs. They agree only while position size is constant.
  // Scale in AFTER a partial exit and they diverge — and on opposite signs this
  // block "repaired" the correct number into its negative.
  //
  // Real case (MES): buy 10 @100, sell 4 @110, buy 6 @120, sell 12 @105.
  // Leg-by-leg realized = +$500; episode VWAPs give entry 107.5 / exit 106.25
  // over qty 16 => computedGross = -$1,000, so a +$500 WINNER stored as a -$500
  // LOSS behind a plausible -1.25 points. Silent, and a $1,000 error.
  let suppliedNet = d.netPnl != null && !isNaN(d.netPnl) ? d.netPnl : null;
  if (suppliedNet != null && suppliedNet !== 0 && computedGross !== 0 && !d.netAuthoritative) {
    const expectedNet = computedGross - fees;
    if (expectedNet !== 0) {
      const expectedPositive = expectedNet > 0;
      const suppliedPositive = suppliedNet > 0;
      if (expectedPositive !== suppliedPositive) {
        suppliedNet = expectedPositive ? Math.abs(suppliedNet) : -Math.abs(suppliedNet);
      }
    }
  }
  // Never subtract fees twice: a supplied net already includes them.
  const net = suppliedNet != null ? suppliedNet : computedGross - fees;
  // With an authoritative net, gross is DERIVED from it so the two cannot
  // contradict (the same reconstruction that broke the sign would otherwise
  // store gross -$1,000 beside net +$500). Identical to computedGross whenever
  // size held constant — a no-op for every episode except the diverging ones.
  const gross = d.netAuthoritative ? net + fees : (computedGross || net + fees);

  const sourceFillIds = d.openFillId || d.closeFillId
    ? { open: d.openFillId ? String(d.openFillId) : null, close: d.closeFillId ? String(d.closeFillId) : null }
    : null;

  const abs = (v: number | null): number | null => (v != null && !isNaN(v) ? Math.abs(v) : null);
  const secs = (v: number | null): number | null => (v != null && !isNaN(v) ? Math.max(0, Math.round(v)) : null);

  return {
    date: d.date, timeIn: d.timeIn || '', timeOut: d.timeOut || '',
    contract: d.contract, contractFull: d.contractFull || d.contract || null,
    side: d.side, qty: d.qty, entry: d.entry, exit: d.exit, points,
    fees, grossPnl: gross, netPnl: net,
    sourceAccount: d.sourceAccount || null,
    sourceFillIds,
    // Magnitudes: an adverse excursion is reported positive.
    maePoints: abs(d.maePoints), mfePoints: abs(d.mfePoints),
    maeSecs: secs(d.maeSecs), mfeSecs: secs(d.mfeSecs),
    feesAuto: !(d.fees != null && !isNaN(d.fees) && d.fees > 0),
    // Attached only when non-empty — the key's ABSENCE is the contract, and
    // downstream code tests for it rather than for an empty array.
    ...(d.excursionSource ? { excursionSource: d.excursionSource } : {}),
    ...(Array.isArray(d.executions) && d.executions.length
      ? { executions: d.executions }
      : {}),
  };
}

/** A source-supplied excursion column, or null when the source has none. */
function srcExcursion(row: Row, which: string): number | null {
  return getCol(row, which) !== '' ? numCol(row, which) : null;
}

/** Symbol exactly as the source wrote it, lightly normalised. */
export const fullContractSym = (raw: string): string => stripSymbolDecorations(raw);

/** Identity of an unfinished position, so re-importing doesn't duplicate it. */
/**
 * 🔴 DO NOT ADD FIELDS TO THIS. IT IS A CROSS-CLIENT, CROSS-VERSION KEY.
 *
 * The string this returns is what `user_preferences.suppressed_open_positions`
 * stores — the list of unfinished positions the trader has permanently
 * dismissed. Beta writes the identical six-field join into the SAME shared
 * column (D5), and `SuppressedPositions.tsx` notes that the fingerprint is the
 * only record kept: the row is reconstructed by taking the string apart.
 *
 * So changing the shape does not "improve" the key — it silently invalidates
 * every dismissal a trader has ever made, in both clients, and the symptom is a
 * warning that reappears with no explanation. `contractFull` was deliberately
 * NOT added here for that reason, even though it is now carried on the position.
 *
 * ▶ Identity-for-suppression and can-this-fill-close-it are two different
 * questions. This answers the first. The second reads `contractFull` directly.
 */
export function openPositionFingerprint(
  p: Omit<OpenPosition, 'fingerprint' | 'contractFull' | 'openFee'>,
): string {
  return [
    p.account || '', p.contract || '', p.side || '', p.openQty || 0,
    Number(p.avgEntry || 0).toFixed(4), p.openTime || '',
  ].join('|');
}

function collectOpenPositions(queues: Map<string, QueueItem[]>): OpenPosition[] {
  const out: OpenPosition[] = [];
  for (const queue of queues.values()) {
    if (!queue.length) continue;
    // Group by side. A healthy queue is single-sided; this is defensive.
    const sides = new Map<string, { side: 'long' | 'short'; qty: number; weighted: number; fee: number; openTime: string; contract: string; contractFull: string | null; account: string }>();
    for (const item of queue) {
      const s = sides.get(item.side) ?? {
        side: item.side, qty: 0, weighted: 0, fee: 0,
        openTime: item.openTime, contract: item.contract,
        contractFull: item.contractFull, account: item.account,
      };
      s.qty += item.qty;
      s.weighted += item.qty * item.entry;
      s.fee += item.fee || 0;
      // Earliest open wins — the position started when its first leg did.
      if (item.openTime && (!s.openTime || new Date(item.openTime) < new Date(s.openTime))) {
        s.openTime = item.openTime;
      }
      sides.set(item.side, s);
    }
    for (const s of sides.values()) {
      const pos = {
        account: s.account || '',
        contract: s.contract,
        side: s.side,
        openQty: s.qty,
        avgEntry: s.qty ? s.weighted / s.qty : 0,
        openTime: s.openTime,
      };
      out.push({
        ...pos, contractFull: s.contractFull, openFee: s.fee,
        fingerprint: openPositionFingerprint(pos),
      });
    }
  }
  return out;
}

/**
 * Pair Sierra fills into round trips.
 *
 * FIFO per `(account, symbol)`. Each (open fill × close fill) pairing emits ONE
 * trade — matching Sierra's own Trade List granularity, so a user who imports
 * both an Order Fills export and a Trade List dedupes correctly. Same-direction
 * fills queue separately rather than VWAP-aggregating, which preserves the
 * distinct entries a scale-in actually had.
 *
 * @param today used only when a date cell is unparseable (D26 — no wall clock)
 */
export function parseSierraFills(
  rowsIn: readonly Row[],
  catalogue: readonly ContractSpec[],
  today: string,
  /**
   * Positions earlier imports left open, for this fill walk to close against.
   *
   * ⚠ DEFAULTS TO NONE, and with none the walk is byte-identical to before —
   * which is what makes this safe to land before any store exists to fill it.
   */
  prior: readonly PriorPosition[] = [],
): SierraResult {
  const rows = dedupeFillsById(rowsIn);

  /**
   * A monotonic sort key in MICROSECONDS.
   *
   * 🚨 `new Date()` TRUNCATES TO MILLISECONDS, and Sierra stamps fills finer than
   * that: `09:32:19.176150` and `09:32:19.176208` both become `…176`. The
   * comparator then returns 0, `Array.sort` leaves them in input order — and the
   * input order is the file's DESCENDING one. **Same-millisecond fills were
   * being paired backwards.**
   *
   * Found by the `PositionQuantity` cross-check below, on Josh's real log: three
   * MES rows where our position and the file's disagreed by a straight swap.
   * ⚠ On that file the money was UNCHANGED — both fills in each pair shared a
   * price and a size, so the A/B came out at +$188.75 MES and −$290.00 MNQ
   * either way. But two fills in one millisecond at DIFFERENT prices is a fast
   * fill, not a rare one, and there the order decides real money.
   *
   * ⚠ `Date.UTC` builds a sort KEY, not an instant. Every row in a file shares
   * one convention so a constant offset cannot reorder them, and reading the
   * stamp as UTC keeps a DST change from shuffling fills across the boundary.
   */
  const fillTs = (r: Row): number => {
    const s = String(getCol(r, 'DateTime', 'FillDateTime', 'FillTime', 'Fill Time', 'Date Time', 'Date') || '').trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})[\sT]+(\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?/.exec(s);
    if (m) {
      const frac = Number(String(m[7] ?? '0').padEnd(6, '0').slice(0, 6)) || 0;
      return Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +m[6]!) * 1000 + frac;
    }
    // Any other shape (US dates, no fraction). Scaled to the same units so the
    // comparator stays homogeneous.
    const t = new Date(s.replace(/\s+/g, ' ')).getTime();
    return Number.isFinite(t) ? t * 1000 : 0;
  };

  // Chronological. Sierra exports newest-first, and FIFO pairing against
  // reverse-ordered fills matches every close to the wrong open.
  const sorted = rows.slice().sort((a, b) => fillTs(a) - fillTs(b));

  const queues = new Map<string, QueueItem[]>();
  const trades: ImportedTrade[] = [];
  let skipped = 0;
  /** Contracts that closed a position opened before this file starts. */
  let carriedIn = 0;
  /** Fills whose `PositionQuantity` could be compared against our own queue. */
  let positionChecked = 0;
  /** Where the exporter's position and ours disagreed. Each one is a real bug. */
  const positionMismatches: PositionMismatch[] = [];
  /** Fills Sierra backfilled from the broker — see `unverifiedFills`. */
  let unverifiedFills = 0;
  /** Fill rows reaching the walker, so the count above reads as a share. */
  let totalFills = 0;
  const openCloseConflicts: OpenCloseConflict[] = [];
  /** Prior positions closed by this file — see `carriedInClosed` on the result. */
  const carriedInClosed: CarriedInClosed[] = [];
  /** Quantity still available on each prior position, so nothing is consumed twice. */
  const priorRemaining = new Map<string, number>();

  for (const r of sorted) {
    const sym = getCol(r, 'Symbol', 'Instrument');
    if (!sym) { skipped++; continue; }

    const acct = getCol(r, 'TradeAccount', 'Account', 'AccountID');
    const dt = getCol(r, 'DateTime', 'FillDateTime', 'FillTime', 'Fill Time', 'Date Time', 'Date');
    const buySell = String(getCol(r, 'BuySell', 'Buy/Sell', 'Side', 'Action', 'OrderAction') || '').toLowerCase();
    const qty = parseInt(
      String(getCol(r, 'FilledQuantity', 'Filled Quantity', 'FillQuantity', 'Fill Quantity', 'Quantity', 'Qty', 'FillQty') || '0')
        .replace(/,/g, ''), 10);
    const px = numCol(r, 'FillPrice', 'Fill Price', 'AverageFillPrice', 'Average Fill Price', 'AveragePrice', 'Price');
    const fee = numCol(r, 'Commission', 'CommissionAmount', 'Fees', 'Total Commission');

    // Two export shapes:
    //   • Trade Activity Log — the real fills are the TERMINAL rows where
    //     OrderStatus='Filled'. Partials that still carry a FillPrice are
    //     excluded so nothing is double-counted; the Filled row already carries
    //     the full quantity at the average fill price.
    //   • Simpler fills export — no OrderStatus; the flag is in ActivityType.
    const orderStatus = String(getCol(r, 'OrderStatus') || '').trim().toLowerCase();
    const activity = String(getCol(r, 'ActivityType', 'Status') || '').toLowerCase();
    const isFillRow = orderStatus ? orderStatus === 'filled' : (!activity || activity.includes('fill'));
    if (!isFillRow) { skipped++; continue; }
    if (!qty || !px) { skipped++; continue; }

    const isBuy = /^b/.test(buySell) || buySell === 'long' || buySell === '1'
      || buySell.includes('bto') || buySell.includes('btc');
    const newSide: 'long' | 'short' = isBuy ? 'long' : 'short';

    const c = findContract(sym, catalogue);
    const dpp = c?.dollarsPerPoint ?? 0;
    const contract = c?.symbol || stripSymbolDecorations(sym);
    const contractFull = fullContractSym(sym);
    // ⚠ SAME PRIORITY AS `dedupeFillsById`, and it did not used to be. That
    // function keys on `FillExecutionServiceID` (the per-FILL id) while this one
    // led with `InternalOrderID` (the per-ORDER id) — so the file was
    // de-duplicated on one column and the trades were stamped from another.
    //
    // The symptom in Josh's data: one trade carrying `openFillId 24006` among
    // rows stamped `1122057`. Two id spaces in one import, which makes the
    // provenance useless for tracing a trade back to its fill and makes the
    // parser's behaviour depend on which columns an export happens to include.
    //
    // ⚠ Nothing KEYS on these — the import fingerprint excludes fill ids because
    // they vary by export source. They are provenance, and this is about them
    // being CONSISTENT rather than about anything they decide.
    const fillId = String(getCol(r, 'FillExecutionServiceID', 'InternalOrderID', 'FillID', 'OrderID', 'Id', 'ID', 'TradeID') || '');

    // Provenance, read BEFORE anything decides on this row. See
    // `fillProvenanceIsVerifiable` and tracker #35.
    const verifiable = fillProvenanceIsVerifiable(
      getCol(r, 'OrderActionSource', 'Order Action Source', 'OrderSource'),
    );
    totalFills++;
    if (!verifiable) unverifiedFills++;

    const openCloseRaw = String(getCol(r, 'OpenClose', 'Open/Close') || '').trim();
    const openClose = openCloseRaw.toLowerCase();

    const key = `${acct || ''}|${sym}`;
    let queue = queues.get(key);
    if (!queue) { queue = []; queues.set(key, queue); }

    let remainingQty = qty;
    let remainingFee = fee;
    /** Did this fill close anything? Needed by the Open/Close cross-check below. */
    let closedAny = false;

    // Close against opposing legs, oldest first, splitting partials.
    while (remainingQty > 0 && queue.length > 0 && (queue[0] as QueueItem).side !== newSide) {
      closedAny = true;
      const head = queue[0] as QueueItem;
      const closeQty = Math.min(head.qty, remainingQty);
      const points = head.side === 'long' ? px - head.entry : head.entry - px;
      const grossPnl = points * dpp * closeQty;
      // Fees split PRO-RATA across the legs each fill actually closed —
      // otherwise a single fill closing three opens charges its whole
      // commission to the first one.
      const allocOpenFee = head.fee * (closeQty / head.qty);
      const allocCloseFee = fee * (closeQty / qty);
      const tradeFees = allocOpenFee + allocCloseFee;

      trades.push(buildImportedTrade({
        date: parseDateOnly(head.openTime, today),
        timeIn: parseTimeOnly(head.openTime),
        timeOut: parseTimeOnly(dt),
        contract, contractFull,
        side: head.side, qty: closeQty,
        entry: head.entry, exit: px,
        fees: tradeFees, netPnl: grossPnl - tradeFees, dpp,
        sourceAccount: acct || null,
        openFillId: head.fillId, closeFillId: fillId,
        maePoints: srcExcursion(r, 'MAE'), mfePoints: srcExcursion(r, 'MFE'),
        maeSecs: srcExcursion(r, 'MAESecs'), mfeSecs: srcExcursion(r, 'MFESecs'),
      }));

      head.qty -= closeQty;
      head.fee -= allocOpenFee;
      remainingQty -= closeQty;
      remainingFee -= allocCloseFee;
      if (head.qty === 0) queue.shift();
    }

    // ── 🚨 OUR PAIRING vs THE FILE'S OWN LABEL ──
    //
    // We just spent this entire fill CLOSING legs. If the file calls it an
    // `Open`, the exporter and this walker disagree about what the fill did —
    // and on Josh's real log that disagreement is the D145 phantom, caught at
    // the exact failing row: the 09:40:20 MNQ buy is marked `Open` with
    // `PositionQuantity=5`, and the old parser spent it closing a phantom short.
    //
    // ⚠ `remainingQty === 0` EXCLUDES REVERSALS. A fill that flips a position
    // genuinely closes and opens in one row, so `Open` on it is not a
    // contradiction — only a fill consumed ENTIRELY by closing is.
    // ⚠ Verifiable rows only: a backfill stamps `Open` on everything and would
    // make this fire on every close in the file. See #35.
    if (closedAny && remainingQty === 0 && verifiable && openClose === 'open') {
      openCloseConflicts.push({
        time: dt, contract, account: acct || '',
        fileSays: openCloseRaw, weDid: 'closed a position', qty,
      });
    }

    // ── 🚨 BEFORE OPENING ANYTHING, ASK WHAT WAS ALREADY HELD (#12) ──
    //
    // A fill with nothing left to close in THIS file may be closing a position
    // an earlier import left open. The app computed that position and threw it
    // away; `prior` is it, handed back in. Consuming it here is what stops the
    // phantom — and unlike the carried-in guard below, it produces a real
    // trade with a real entry price instead of an unvaluable gap.
    //
    // ⚠ TRIED FIRST, ahead of the `OpenClose` guard, on purpose. On a Sierra
    // file a fill marked `Close` with an empty queue is exactly this case; the
    // guard's answer (count it and move on) is the fallback for when nothing is
    // on record, not the preferred one.
    if (remainingQty > 0 && prior.length) {
      const legs = consumePrior(prior, priorRemaining, {
        account: acct || '', contract, contractFull, side: newSide,
        qty: remainingQty, time: dt,
      });
      for (const leg of legs) {
        const p = leg.position;
        const points = p.side === 'long' ? px - p.avgEntry : p.avgEntry - px;
        const grossPnl = points * dpp * leg.qty;
        // The opening commission travelled with the position — see
        // `PriorPosition.openFee`. Without it the trade reports at half cost.
        const tradeFees = leg.openFee + fee * (leg.qty / qty);
        trades.push(buildImportedTrade({
          date: parseDateOnly(p.openTime, today),
          timeIn: parseTimeOnly(p.openTime),
          timeOut: parseTimeOnly(dt),
          contract, contractFull,
          side: p.side, qty: leg.qty,
          entry: p.avgEntry, exit: px,
          fees: tradeFees, netPnl: grossPnl - tradeFees, dpp,
          sourceAccount: acct || null,
          openFillId: '', closeFillId: fillId,
          maePoints: null, mfePoints: null, maeSecs: null, mfeSecs: null,
        }));
        carriedInClosed.push({
          time: dt, contract, account: acct || '',
          qty: leg.qty, openedAt: p.openTime, fingerprint: p.fingerprint,
        });
        remainingQty -= leg.qty;
        remainingFee -= fee * (leg.qty / qty);
      }
    }

    // Anything left opens (or extends, or reverses into) a position.
    if (remainingQty > 0) {
      // ── 🚨 A CLOSING FILL WITH NOTHING TO CLOSE IS A CARRIED-IN POSITION ──
      //
      // The exporter says which side of the position each fill was on. When it
      // says CLOSE and the queue is empty, the position was opened BEFORE this
      // file begins — an overnight hold, or a part-day export. There is no entry
      // price for it, so it cannot be valued.
      //
      // Opening a phantom OPPOSITE position instead is the worst answer
      // available, because the error does not stop at one trade: the phantom
      // eats the next real OPENING fill, which then cannot open, so the fill
      // after that opens a phantom too. **THE WHOLE DAY SHIFTS BY ONE LEG.**
      //
      // Measured on Josh's real 2026-08-05 log, whose last line is an Aug-4
      // 23:30 sell of 5 MNQ marked `Close` — an overnight long being flattened:
      //
      //                | before      | after      | his ATAS export
      //   long lots    | 0           | 5          | 5 (+10 still open)
      //   short lots   | 47          | 37         | 37
      //   gross P&L    | +$1,459.50  | -$290.00   | -$290.00
      //
      // A $1,749.50 swing on one instrument on one day, in the flattering
      // direction, on the account that blew. With the guard, all sixteen round
      // trips match his ATAS export to the cent.
      //
      // ⚠ ONLY on an explicit `Close`. Exports with no OpenClose column keep the
      // old behaviour, because there we genuinely cannot tell a carried-in close
      // from a new short — and dropping opening trades from every such file
      // would be a worse bug than the one being fixed.
      //
      // 🚨 AND `OpenClose` IS ONLY EVIDENCE WHEN THE ROW CAN BE BELIEVED (#35).
      // A backfilled row stamps `Open` on everything, closes included, so a
      // `Close` there would be the one value the source demonstrably does not
      // set correctly. Withdrawing trust costs nothing on a real file — none of
      // the 55 backfill rows in Josh's 2026-08-05 export says `Close` — but it
      // stops the guard being satisfied by a column known to be unreliable.
      if (openClose === 'close' && verifiable) { carriedIn += remainingQty; continue; }

      queue.push({
        side: newSide, qty: remainingQty, entry: px, openTime: dt,
        fee: remainingFee, fillId, contract, contractFull, account: acct || '', dpp,
      });
    }

    // ── 🚨 THE EXPORTER'S OWN ANSWER, USED AS A CHECK ──
    //
    // `PositionQuantity` is the position AFTER this fill. The queue is what we
    // think it is. When they disagree, our pairing is wrong — and this is the
    // one place that can be known AT IMPORT TIME rather than by a human noticing
    // a strange number weeks later.
    //
    // It catches D145 at the exact failing row: with the carried-in guard
    // removed, the 09:40 buy is consumed closing a phantom short and the queue
    // is EMPTY while this column says 5.
    //
    // ⚠ SPARSE, AND THE COVERAGE IS REPORTED RATHER THAN IMPLIED. On Josh's real
    // log only 14% of processed rows carry it — Sierra populates it on its own
    // order records but not on fills pulled back from the broker. **A silent
    // pass is not proof the day is right**, which is why `positionChecked` is
    // returned beside the mismatches.
    //
    // ⚠ COMPARED AS A MAGNITUDE, because the column is unsigned. It cannot tell
    // long 5 from short 5 — only that the SIZE is wrong. An off-by-one-leg
    // cascade always changes the size, so that is enough for the case it exists
    // to catch.
    const pqRaw = String(getCol(r, 'PositionQuantity', 'Position Quantity') || '').trim();
    if (pqRaw !== '') {
      const expected = Math.abs(parseInt(pqRaw.replace(/,/g, ''), 10));
      if (Number.isFinite(expected)) {
        positionChecked++;
        const held = queue.reduce((s, q) => s + q.qty, 0);
        if (held !== expected) {
          positionMismatches.push({ time: dt, contract, account: acct || '', expected, held });
        }
      }
    }
  }

  return {
    trades, openPositions: collectOpenPositions(queues),
    skipped, carriedIn, positionChecked, positionMismatches,
    unverifiedFills, totalFills, openCloseConflicts, carriedInClosed,
  };
}
